import Image from 'next/image';
import type { ComponentProps, FC, ReactNode } from 'react';
import { ImagesService } from 'service/images/imagesService';
import { DESKTOP_MEDIA_BP, FALLBACK_IMAGE } from '../../config/config';
import { AiBadgeButton } from '../AiDisclosure/AiBadgeButton';

import FallBackImageForNoImage from './assets/fallback_image.webp';
import { AiLabelType } from '@/types/Recipes';

// Image that shows up before we load FallBackImageForNoImage
const blurredFallbackImage = FALLBACK_IMAGE;

const getSizesForImage = (sizes?: string) => {
  return !sizes ? '(max-width: 768px) 33vw, (max-width: 1200px) 50vw, 100vw' : sizes;
};

const _BPs = [
  '[@media(max-width:767px)]:block',
  '[@media(min-width:768px)]:block',
  '[@media(min-width:1200px)]:block',
  '[@media(min-width:1400px)]:block',
  '[@media(min-width:1680px)]:block',
  '[@media(min-width:1920px)]:block',
] as const;
type ImageProps = { src: string; sources?: undefined };
type PictureProps = {
  sources?: {
    srcSet?: string;
    media?: string;
    type?: string;
    width?: number;
    height?: number;
  }[];
  children?: ReactNode;
  src?: undefined;
};
type CommonPictureImageProps = {
  className?: string;
  preferredWidth?: number;
  mimeType?: string;
  dataTestId?: string;
  ratioClasses?: string;
  skipSizes?: boolean;
  aiLabeling?: boolean | null;
  aiLabelingText?: string;
  aiLabelingTitle?: string;
  aiLabelType?: AiLabelType;
} & Omit<ComponentProps<typeof Image>, 'src'>;

export type Props = (ImageProps | PictureProps) & CommonPictureImageProps;

const NextImage: FC<Props> = ({ alt, className, children, ...props }) => {
  const {
    preferredWidth,
    mimeType,
    skipSizes = false,
    aiLabeling,
    aiLabelingText,
    aiLabelingTitle,
    aiLabelType,
    ...rest
  } = props;
  const imageShouldBeFill = !props.width && !props.height;

  const buildAlt = (base?: string | null) => {
    if (!aiLabeling) return base ?? '';
    const text = base ? `${base}` : '';
    return text ? `${text}, AI-labeled image` : 'AI-labeled image';
  };

  if (props.src) {
    const serveImageUnoptimized = ImagesService.isNextImageUnoptimized(props.src as string);

    const evaluatedSrc = ImagesService.transformUrlToProxyUrl(props.src as string);

    return (
      <>
        <Image
          {...rest}
          alt={buildAlt(alt)}
          fill={imageShouldBeFill}
          width={!imageShouldBeFill ? props.preferredWidth || props.width : undefined}
          height={!imageShouldBeFill ? props.height || 600 : undefined}
          className={className}
          sizes={skipSizes || serveImageUnoptimized ? undefined : getSizesForImage(props.sizes)}
          quality={props.quality || 75}
          unoptimized={serveImageUnoptimized} // we're already optimizing the image
          loader={() => props.src as string}
          src={evaluatedSrc}
        />
        {aiLabeling ? (
          <AiBadgeButton text={aiLabelingText} title={aiLabelingTitle} type={aiLabelType} />
        ) : null}
      </>
    );
  }

  if (props.sources?.length) {
    const uniqueObjArraySources = [
      ...new Map(props.sources.map((item) => [item.media, item])).values(),
    ];

    const filteredUniqueObjArraySources = uniqueObjArraySources.filter(
      (source): source is typeof source & { srcSet: string } => Boolean(source.srcSet),
    );
    const fallbackSource =
      filteredUniqueObjArraySources.find((source) => source.media === DESKTOP_MEDIA_BP) ??
      filteredUniqueObjArraySources.at(-1);

    if (!fallbackSource?.srcSet) {
      return null;
    }

    const fallbackSrc = ImagesService.transformUrlToProxyUrl(fallbackSource.srcSet);
    const serveImageUnoptimized = ImagesService.isNextImageUnoptimized(fallbackSource.srcSet);
    const width = fallbackSource.width ?? props.width;
    const height = fallbackSource.height ?? props.height;

    return (
      <div className={`relative w-full h-full ${props.ratioClasses ?? ''}`}>
        <picture>
          {filteredUniqueObjArraySources.map((source, index) => (
            <source
              key={`${source.media ?? 'default'}-${index}`}
              media={source.media}
              srcSet={ImagesService.transformUrlToProxyUrl(source.srcSet)}
              type={source.type}
            />
          ))}
          <Image
            src={fallbackSrc}
            alt={buildAlt(alt)}
            width={width}
            height={height}
            fill={!width || !height}
            quality={props.quality || 75}
            unoptimized={serveImageUnoptimized}
            sizes={skipSizes || serveImageUnoptimized ? undefined : getSizesForImage(props.sizes)}
            loader={() => fallbackSrc}
            className={className}
            loading={props.priority ? 'eager' : props.loading}
            fetchPriority={props.priority ? 'high' : props.fetchPriority}
            placeholder={props.placeholder}
            blurDataURL={props.blurDataURL}
          />
        </picture>
        {children}
        {aiLabeling ? (
          <AiBadgeButton text={aiLabelingText} title={aiLabelingTitle} type={aiLabelType} />
        ) : null}
      </div>
    );
  }

  return (
    <Image
      src={FallBackImageForNoImage}
      alt="Logo Image"
      placeholder="blur"
      blurDataURL={blurredFallbackImage}
      fill
      className={className}
      sizes={getSizesForImage()}
    />
  );
};

export const customImageLoader = ({
  src,
  width,
  quality,
}: {
  src: string;
  width: number;
  quality?: number;
}) => {
  return `${src}?w=${width}&q=${quality || 75}`;
};

export default NextImage;
